/* eslint-disable eslint-comments/disable-enable-pair -- needed */
/* eslint-disable no-unsafe-optional-chaining -- needed */
/* eslint-disable complexity -- needed */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import {
  ComponentLevelNotification,
  Button,
  Checkbox,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  InfoTooltipButton,
  Heading,
} from '@americanexpress/dls-react';
import CardMemberInfo from '../../CardMemberInfo';
import { Layout } from '../layout';
import { renderHTML } from '../../../../../utilities/render-html';
import useOneData from '../../../../../hooks/useOneData';
import useDisableButton from '../../../../../hooks/useDisableButton';
import {
  getEndpoint,
  getEnrollmentRequestBody,
} from '../../../../../api/api-utils';
import {
  CreateLoyaltyBenefitEnrollmentStatusVx,
  suppressCheckboxForLocaleList,
} from '../../../../../constants';

export function MembershipInputRequiredApply(props) {
  const [checkbox, toggleCheckBox] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [agreeError, setAgreeError] = useState(false);
  const [accountIdError, setAccountIdError] = useState(false);
  const [inputWarning, setInputWarning] = useState('');
  const intl = useIntl(); // Hook for accessing locale content, intl.formatMessage()

  const [success, setSuccess] = useState(false);
  const stepTransition = props.stepTransition;
  const selectedProduct = props.selectedProduct;
  const inputctatext = props.inputctatext;
  const label = props.callToAction?.INTERNAL.label || props.callToAction?.ANCHOR?.label || 'Enroll Now';
  const accountField = props?.requiredFields?.account?.memberAccount;
  const accountType = accountField?.type;
  const accountMinLength = accountField?.minLength || 0;
  const accountMaxLength = accountField?.maxLength || 19;
  const toolTip = accountField?.toolTip;
  const invalidCharWarning = accountType === 'number' ? 'Please enter numbers only.' : 'Please enter letters and numbers only.';
  const invalidLengthWarning = `Enter a ${accountMinLength}-${accountMaxLength} digit account number.`;

  const languageData = props?.languageData;
  const description = props?.description;
  const { isFetching, error, data } = props?.cardData;
  const safeDescription = renderHTML(description);

  const faasApiUrl = props?.faasApiUrl;
  const sorBenefitId = props.sorBenefitId;
  const benefitName = props.benefitName;
  const cardname = props.cardname;
  const locale = props?.locale;
  const shouldSuppressCheckbox = suppressCheckboxForLocaleList.includes(locale);
  const benefitTitle = props?.benefitTitle;
  const endpoint = getEndpoint(
    faasApiUrl,
    CreateLoyaltyBenefitEnrollmentStatusVx
  );
  const requestOptions = {
    membershipNumber: inputValue,
    benefitName,
    cardname,
  };
  const requestBody = getEnrollmentRequestBody(
    selectedProduct,
    sorBenefitId,
    locale,
    requestOptions
  );
  const parseEntities = (txt) => new DOMParser().parseFromString(txt, 'text/html').body.textContent;

  const endpointOptions = {
    body: requestBody,
    defer: true, // defers fetchye call until run function is invoked
  };
  const { isLoading, run } = useOneData(endpoint, endpointOptions);
  const { isDisabledAfterSubmit, disableButton } = useDisableButton();

  const validate = (input = '') => {
    const inputLength = input.length;
    let validateRegex;
    if (accountType === 'number') {
      validateRegex = /^\d+$/;
    } else if (accountType === 'alphanumeric') {
      validateRegex = /^[\dA-Za-z]+$/;
    } else {
      validateRegex = /^\w+$/;
    }
    const isInvalidRegexInput = !validateRegex.test(input);
    // Check is ported from legacy and kept this way to maintain business as usual. Check ensures 20+ chars.
    const invalidLength = inputLength < accountMinLength || inputLength > accountMaxLength;

    if (isInvalidRegexInput) {
      // invalid char
      setInputWarning(invalidCharWarning);
      return false;
    }

    if (invalidLength) {
      setInputWarning(invalidLengthWarning);
      return false;
    }

    setInputWarning('');
    return true;
  };

  // eslint-disable-next-line consistent-return -- needed
  const submit = async () => {
    const isValidSubmit = checkbox && inputValue !== '';
    const showAgreeError = !checkbox;
    const showAccountIdError = inputValue === '';

    if (showAccountIdError) {
      return setAccountIdError(true);
    }

    if (showAgreeError && !shouldSuppressCheckbox) {
      return setAgreeError(true);
    }

    if (shouldSuppressCheckbox || isValidSubmit) {
      setAgreeError(false);
      setAccountIdError(false);
      disableButton();
      const { data: enrollmentSuccess, error: enrollmentError } = await run();
      if (enrollmentSuccess) {
        stepTransition(true, false);
      }
      if (enrollmentError) {
        stepTransition(false, true);
      }
    }
  };

  const onChangeInput = (value) => {
    const valid = validate(value);
    setSuccess(valid);
    setInputValue(value);
    setAccountIdError(false);
  };

  const onChangeCheckbox = () => {
    if (!checkbox) {
      if (agreeError) {
        setAgreeError(false);
      }
      toggleCheckBox(true);
    } else {
      toggleCheckBox(false);
    }
  };

  const isError = inputWarning || accountIdError || agreeError;

  return (
    <>
      <section className="pad-2-lr text-justify">
        <Heading level={2} variant="sans-small-regular" className="text-align-center margin-0 margin-3-b">
          {renderHTML(benefitTitle)}
        </Heading>
        {safeDescription && <article className="margin-3-b">{safeDescription}</article>}
      </section>
      <Layout>
        {!isFetching && !error && (
          <CardMemberInfo {...data} languageData={languageData} />
        )}
        <section className="margin-t">
          <Label for="membershipNumber">{inputctatext}</Label>
          <span> (Required)</span>
          {toolTip && (
            <span>
              <Tooltip disableHover={true} id="membershipNumberTooltip" placement="top">
                <TooltipTrigger>
                  <InfoTooltipButton
                    data-testid="tooltip-btn-membershipNumber"
                    className="margin-1-l"
                    aria-label={intl.formatMessage({
                      id: 'view-all.disclaimer.title',
                    })}
                  />
                </TooltipTrigger>
                <TooltipContent>{toolTip}</TooltipContent>
              </Tooltip>
            </span>
          )}

          <section className="pad-2-b">
            <Input
              id="membershipNumber"
              data-testid="membershipId"
              onChange={(e) => onChangeInput(e.target.value)}
              success={success}
              status={inputWarning ? 'error' : 'default'}
              required={true}
            />
            {inputWarning && (
              <ComponentLevelNotification className="pad-1-t">
                {inputWarning}
              </ComponentLevelNotification>
            )}
            {accountIdError && (
            <ComponentLevelNotification className="pad-2-t">
              <p>Please enter a valid loyalty account number.</p>
            </ComponentLevelNotification>
            )}
          </section>
          {shouldSuppressCheckbox || intl.formatMessage({
            id: 'hide-ui.tnc-checkbox',
            defaultMessage: '',
          }) === 'true'
            ? ''
            : (
              <Checkbox
                id="enrollment-checkbox"
                data-testid="enrollment-checkbox"
                label={`${intl.formatMessage({ id: 'shared.terms-links.terms-and-conditions', defaultMessage: 'I understand and agree to the terms and conditions.‡' })}`}
                reactlytics={{
                  element: `${cardname}>>${benefitName}>>agree-to-terms`,
                }}
                onChange={onChangeCheckbox}
              />
            )}
        </section>
        {agreeError && (
        <ComponentLevelNotification className="pad-2-t">
          Please agree to the Terms & Conditions to continue.
        </ComponentLevelNotification>
        )}
        <ExtendReactlytics
          analytics={{
            event: {
              meta: {
                benefitname: `${parseEntities(benefitTitle)?.replace(/ /g, '-').toLowerCase()}`,
              },
            },
          }}
        >
          <Button
            id="enrollsubmit"
            className="flex flex-column flex-align-items-center margin-auto-lr margin-3-tb"
            reactlytics={{
              element: `cta>>enroll-submit>>${cardname?.replace(/ /g, '-').toLowerCase()}`,
              type: 'impression',
            }}
            onClick={submit}
            aria-disabled={inputWarning || accountIdError || agreeError || isDisabledAfterSubmit}
            aria-describedby={isError ? 'Please enter a value above.' : null}
            working={isLoading}
            aria-label={`${`${intl.formatMessage({ id: label })} ${benefitName}`}`}
            variant="primary"
          >
            {intl.formatMessage({
              id: `shared.terms-links.${label.replace(/ /g, '-').toLowerCase()}`,
              defaultMessage: label,
            })}
          </Button>
        </ExtendReactlytics>
      </Layout>
    </>
  );
}
